import React, { useEffect } from "react";
import { MenuProps } from "@mui/material";

import { FileEntity, Space, Track } from "@highnote/server/src/core/entities";
import { DefaultAttachmentActions } from "App/common/AttachmentActions";
import { DefaultSpaceActions } from "App/common/SpaceActions";
import {
  ENTITY_TYPE,
  EntityRowConfig,
} from "App/components/EntityTable/config";
import { TrackVersionActions } from "App/components/TrackEditor/TrackVersions/TrackVersionActions";
import { DefaultTrackActions } from "App/components/useEntities/useLibraryTracks/TrackActions";
import { useEntitiesSelection } from "App/components/useEntitiesSelection";
import { EntitySelection } from "../EntityLibrary/EntitySelection";

type Props = {
  row: EntityRowConfig;
  anchorPosition: MenuProps["anchorPosition"] | null;
  onClose: () => void;
  hasEntitiesSelection?: boolean;
};

type ContextMenuComponentProps = Pick<Props, "row"> & { menuProps: MenuProps };

const renderContextMenu = ({ row, menuProps }: ContextMenuComponentProps) => {
  switch (row.type) {
    case ENTITY_TYPE.SPACE:
      return (
        <DefaultSpaceActions
          hideTriggerButton
          space={row.entity as Space}
          menuProps={menuProps}
        />
      );
    case ENTITY_TYPE.TRACK:
      return (
        <DefaultTrackActions
          hideTriggerButton
          track={row.entity as Track}
          viewingSpaceId={row.viewingSpaceId}
          menuProps={menuProps}
        />
      );
    case ENTITY_TYPE.FILE:
      return (
        <DefaultAttachmentActions
          hideTriggerButton
          attachment={row.entity as FileEntity}
          menuProps={menuProps}
        />
      );
    case ENTITY_TYPE.TRACK_VERSION: {
      const version = row.entity as FileEntity;
      const track = row.parentEntity as Track;
      return (
        <TrackVersionActions
          hideTriggerButton
          version={version}
          track={track}
          menuProps={menuProps}
        />
      );
    }
    default:
      return null;
  }
};

// Basic context menu that doesn't use selection context
const SingleEntityContextMenu = ({
  row,
  menuProps,
}: ContextMenuComponentProps) => {
  return renderContextMenu({ row, menuProps });
};

// MultiEntityContextMenu is a menu that has access to EntitiesSelectionContext.
// This allows it to display the action menu for selected entities.
const MultiEntityContextMenu = ({
  row,
  menuProps,
}: ContextMenuComponentProps) => {
  const { selectedEntities } = useEntitiesSelection();

  if (Object.keys(selectedEntities).length > 0) {
    return <EntitySelection hideTriggerButton menuProps={menuProps} />;
  }

  return renderContextMenu({ row, menuProps });
};

export const ContextMenuActions = ({
  row,
  anchorPosition,
  onClose,
  hasEntitiesSelection = false,
}: Props) => {
  if (!anchorPosition) return null;

  const menuProps: MenuProps = {
    open: true,
    onClose,
    keepMounted: true,
    // Manual anchor position is used to position the context menu relative to the
    // anchor element.
    anchorPosition,
    anchorReference: "anchorPosition",
    // anchorOrigin is used to position the context menu relative to the anchor element.
    anchorOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  useEffect(() => {
    return () => {
      // This should help with the unmounting error when the context menu is closed.
      onClose();
    };
  }, []);

  // Conditionally render based on whether selection context is available
  if (hasEntitiesSelection) {
    return <MultiEntityContextMenu row={row} menuProps={menuProps} />;
  }

  return <SingleEntityContextMenu row={row} menuProps={menuProps} />;
};
